import React from 'react';

export const SOCIAL_LINKS = {
  linkedin: "https://www.linkedin.com/company/aksu-business-school",
  facebook: "https://www.facebook.com/aksu-business-school"
};

export const LinkedInIcon = ({ className = "w-4 h-4", fill = "currentColor" }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill={fill} 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
  </svg>
);

export const FacebookIcon = ({ className = "w-4 h-4", fill = "currentColor" }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill={fill} 
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
  </svg>
);

/**
 * Standardised Compact Social Icon Links (Header Top Bar, Mobile Drawer)
 */
export const SocialIconLinks = ({ 
  className = "flex items-center space-x-2",
  iconClassName = "w-3.5 h-3.5",
  theme = "header" // 'header' | 'footer' | 'light' | 'gold'
}) => {
  const getStyles = () => {
    switch (theme) {
      case 'footer':
        return {
          li: "w-8 h-8 rounded-lg bg-slate-800 hover:bg-[#0077B5] text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-700 hover:border-[#0077B5] hover:scale-105 shadow-sm",
          fb: "w-8 h-8 rounded-lg bg-slate-800 hover:bg-[#1877F2] text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-700 hover:border-[#1877F2] hover:scale-105 shadow-sm"
        };
      case 'gold':
        return {
          li: "w-7 h-7 rounded-md bg-[#7A470E]/20 hover:bg-[#0077B5] text-[#B87A28] hover:text-white flex items-center justify-center transition-all border border-[#7A470E]/40 hover:border-transparent",
          fb: "w-7 h-7 rounded-md bg-[#7A470E]/20 hover:bg-[#1877F2] text-[#B87A28] hover:text-white flex items-center justify-center transition-all border border-[#7A470E]/40 hover:border-transparent"
        };
      case 'light':
        return {
          li: "w-8 h-8 rounded-xl bg-white hover:bg-[#0077B5] text-[#0B2644] hover:text-white flex items-center justify-center transition-all border border-slate-200 shadow-sm hover:scale-105",
          fb: "w-8 h-8 rounded-xl bg-white hover:bg-[#1877F2] text-[#0B2644] hover:text-white flex items-center justify-center transition-all border border-slate-200 shadow-sm hover:scale-105"
        };
      case 'header':
      default:
        return {
          li: "p-1 rounded text-slate-300 hover:text-[#0077B5] hover:bg-white/10 transition-colors",
          fb: "p-1 rounded text-slate-300 hover:text-[#1877F2] hover:bg-white/10 transition-colors"
        };
    }
  };

  const styles = getStyles();

  return (
    <div className={className}>
      <a 
        href={SOCIAL_LINKS.linkedin} 
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Official LinkedIn: Akwa Ibom State University Business School"
        title="Follow on LinkedIn (Official Page)"
        className={styles.li}
      >
        <LinkedInIcon className={iconClassName} />
      </a>
      <a 
        href={SOCIAL_LINKS.facebook} 
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Official Facebook: Akwa Ibom State University Business School"
        title="Follow on Facebook (Official Page)"
        className={styles.fb}
      >
        <FacebookIcon className={iconClassName} />
      </a>
    </div>
  );
};

/**
 * Standardised Official Social Channels Block (Unified Executive Icon Design for Footer & Official Channels)
 */
export const SocialStandardisedLinks = ({ className = "flex items-center space-x-2.5" }) => {
  return (
    <div className={className}>
      <a
        href={SOCIAL_LINKS.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className="w-8 h-8 rounded-xl bg-slate-900 hover:bg-[#0077B5] text-[#0077B5] hover:text-white border border-slate-700/80 hover:border-[#0077B5] flex items-center justify-center transition-all duration-200 shadow-sm hover:scale-110 group cursor-pointer"
        title="Connect with AKSU Business School on LinkedIn"
        aria-label="Official LinkedIn"
      >
        <LinkedInIcon className="w-4 h-4 fill-current" />
      </a>

      <a
        href={SOCIAL_LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="w-8 h-8 rounded-xl bg-slate-900 hover:bg-[#1877F2] text-[#1877F2] hover:text-white border border-slate-700/80 hover:border-[#1877F2] flex items-center justify-center transition-all duration-200 shadow-sm hover:scale-110 group cursor-pointer"
        title="Follow AKSU Business School on Facebook"
        aria-label="Official Facebook"
      >
        <FacebookIcon className="w-4 h-4 fill-current" />
      </a>
    </div>
  );
};

// Backwards-compatible export
export const SocialPillButtons = SocialStandardisedLinks;
